import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "@moneyforward/mfx_biz_react_components";
import { EmailFormField, TermsFormField } from "@/components/molecules";
import React, { useEffect, useState } from "react";
import { useIsSpMode } from "@/hooks";
import { css } from "@emotion/react";
import { color } from "@/styles/theme";
import {
  GetBizsolEmailQuery,
  useGetBizsolEmailLazyQuery,
} from "@/generated/graphql";
import config from "@/libs/config";
import useRollbarUtil from "../hooks/useRollbarUtil";

export type BizsolProvisionalRegistrationFormValues = {
  email: string;
  termOfUse: boolean;
  specialTerm: boolean;
  privacyPolicy: boolean;
  adEmailDelivery: boolean;
};

type Props = {
  onSubmit: (formValues: BizsolProvisionalRegistrationFormValues) => void;
};

export const BizsolProvisionalRegistrationForm = (props: Props) => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const isSpMode = useIsSpMode();

  const methods = useForm<BizsolProvisionalRegistrationFormValues>({
    reValidateMode: "onBlur",
  });

  // ------------------------------------------------------------
  // Get user's email from Bizsol and use it as a default value
  // ------------------------------------------------------------
  // Handle get bizsol email completed
  const handleGetBizsolEmailCompleted = (data: GetBizsolEmailQuery) => {
    if (data) {
      data.bizSolUserInfo?.email &&
        methods.setValue("email", data.bizSolUserInfo.email);
    }
  };

  // Declare get bizsol email lazy query
  const [getBizsolEmailQuery] = useGetBizsolEmailLazyQuery({
    onCompleted: handleGetBizsolEmailCompleted,
    onError: getNotifyErrorHandlerForGraphQL("Failed to get bizsol email"),
  });

  // Side effect to get email from bizsol
  const router = useRouter();
  useEffect(() => {
    if (router.isReady) {
      const { code } = router.query;
      const codeStr = Array.isArray(code) ? code[0] : code;
      getBizsolEmailQuery({
        variables: {
          registrationCode: codeStr,
        },
      });
    }
  }, [router.isReady]);

  // ------------------------------------------------------------
  // Update agreed value from formValues changing
  // ------------------------------------------------------------
  const [agreed, setAgreed] = useState(false);
  const formValues = methods.getValues();
  useEffect(() => {
    setAgreed(
      formValues.termOfUse &&
        formValues.specialTerm &&
        formValues.privacyPolicy &&
        formValues.adEmailDelivery
    );
  }, [formValues]);

  return (
    <>
      <div css={container}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(props.onSubmit)} css={formCSS}>
            <div css={!isSpMode ? borderBox : borderSpModeBox}>
              <div css={!isSpMode ? inputList : spModeInputList}>
                <EmailFormField name="email" />
              </div>
            </div>
            <div css={!isSpMode ? box : spModeBox}>
              <TermsFormField
                termOfUse="termOfUse"
                specialTerm="specialTerm"
                privacyPolicy="privacyPolicy"
                adEmailDelivery="adEmailDelivery"
                usingExternalIdp={config.USING_EXTERNAL_IDP}
              />
            </div>
            <div css={buttonWrapper}>
              <Button
                type="submit"
                disabled={methods.formState.isSubmitting || !agreed}
              >
                登録
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
};

const signInLink = css({
  textAlign: "center",
});

const container = css({
  display: "flex",
  justifyContent: "center",
});

const box = css({
  width: "640px",
  margin: "0 auto",
});

const spModeBox = css({
  padding: "15px 17px",
});

const borderBox = css(box, {
  border: `1px solid ${color.iron}`,
  padding: "32px",
  borderRadius: "4px",
});

const borderSpModeBox = css(spModeBox, {
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const formCSS = css({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
});

const inputList = css({
  flex: 1,
});

const spModeInputList = css({
  flex: 1,
});

const buttonWrapper = css({
  margin: "40px auto",
});
